import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Progress } from "@/components/ui/progress";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Separator } from "@/components/ui/separator";
import { 
  ClipboardCheck, ArrowRight, ArrowLeft, Phone, MessageSquare, 
  FileText, Sparkles, AlertTriangle, CheckCircle, XCircle,
  Loader2, Heart, Brain, Activity, Stethoscope
} from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const DISEASE_CATEGORIES = [
  { id: "general", name: "General Hospice Criteria", icon: ClipboardCheck, color: "bg-primary" },
  { id: "cancer", name: "Cancer", icon: Activity, color: "bg-red-500" },
  { id: "heart_failure", name: "Heart Failure / CHF", icon: Heart, color: "bg-pink-500" },
  { id: "dementia", name: "Dementia / Alzheimer's", icon: Brain, color: "bg-purple-500" },
  { id: "copd", name: "COPD / Pulmonary", icon: Activity, color: "bg-blue-500" },
  { id: "renal", name: "Renal Disease", icon: Stethoscope, color: "bg-yellow-500" },
  { id: "als", name: "ALS / Neuromuscular", icon: Activity, color: "bg-green-500" }
];

export default function HospiceCriteria() {
  const navigate = useNavigate();
  const [step, setStep] = useState("select"); // select, questionnaire, results
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [questionnaire, setQuestionnaire] = useState(null);
  const [answers, setAnswers] = useState({});
  const [patientName, setPatientName] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);

  const loadQuestionnaire = async (categoryId) => {
    setIsLoading(true);
    try {
      const response = await api.get(`/facility/criteria/${categoryId}`);
      setQuestionnaire(response.data.questionnaire);
      setSelectedCategory(categoryId);
      setStep("questionnaire");
      setAnswers({});
      setCurrentQuestionIndex(0);
    } catch (error) {
      toast.error("Failed to load questionnaire");
    } finally {
      setIsLoading(false);
    }
  };

  const handleAnswer = (questionId, value) => {
    setAnswers(prev => ({ ...prev, [questionId]: value }));
  };

  const submitAssessment = async () => {
    setIsLoading(true);
    try {
      const token = localStorage.getItem("facility_token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      const response = await api.post("/facility/criteria/analyze", {
        disease_category: selectedCategory,
        patient_name: patientName || "Anonymous",
        answers: Object.entries(answers).map(([question_id, answer]) => ({
          question_id,
          answer
        }))
      }, { headers });
      
      setResults(response.data);
      setStep("results");
    } catch (error) {
      toast.error("Failed to analyze criteria");
    } finally {
      setIsLoading(false);
    }
  };

  const resetAssessment = () => {
    setStep("select");
    setSelectedCategory(null);
    setQuestionnaire(null);
    setAnswers({});
    setResults(null);
    setPatientName("");
    setCurrentQuestionIndex(0);
  };

  const getUrgencyColor = (urgency) => {
    const colors = {
      high: "text-red-600 bg-red-50 border-red-200",
      moderate: "text-yellow-600 bg-yellow-50 border-yellow-200",
      low: "text-blue-600 bg-blue-50 border-blue-200",
      none: "text-gray-600 bg-gray-50 border-gray-200"
    };
    return colors[urgency] || colors.none;
  };

  const getUrgencyIcon = (urgency) => {
    const icons = {
      high: <AlertTriangle className="w-6 h-6" />,
      moderate: <Activity className="w-6 h-6" />,
      low: <FileText className="w-6 h-6" />,
      none: <XCircle className="w-6 h-6" />
    };
    return icons[urgency] || icons.none;
  };

  // Category Selection Step
  if (step === "select") {
    return (
      <div className="min-h-screen bg-gradient-to-b from-background to-muted/20">
        
        
        <div className="container mx-auto px-4 py-12">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <Badge className="mb-4" variant="secondary">
                <Sparkles className="w-3 h-3 mr-1" /> AI-Powered Assessment
              </Badge>
              <h1 className="text-3xl font-bold mb-4">Hospice Eligibility Criteria Tool</h1>
              <p className="text-lg text-muted-foreground">
                Use our AI-powered tool to assess hospice eligibility based on LCD guidelines. 
                Select a category to begin the assessment.
              </p>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              {DISEASE_CATEGORIES.map((category) => {
                const Icon = category.icon;
                return (
                  <Card 
                    key={category.id}
                    className="cursor-pointer hover:shadow-lg transition-all hover:border-primary"
                    onClick={() => loadQuestionnaire(category.id)}
                    data-testid={`criteria-${category.id}`}
                  >
                    <CardContent className="pt-6">
                      <div className="flex items-center gap-4">
                        <div className={`w-12 h-12 rounded-xl ${category.color} flex items-center justify-center`}>
                          <Icon className="w-6 h-6 text-white" />
                        </div>
                        <div className="flex-1">
                          <h3 className="font-semibold">{category.name}</h3>
                          <p className="text-sm text-muted-foreground">
                            {category.id === "general" 
                              ? "LCD-based general eligibility criteria"
                              : `Disease-specific criteria for ${category.name.toLowerCase()}`
                            }
                          </p>
                        </div>
                        <ArrowRight className="w-5 h-5 text-muted-foreground" />
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>

            {/* Contact Info */}
            <Card className="mt-8">
              <CardContent className="pt-6">
                <div className="flex flex-col md:flex-row items-center justify-between gap-4">
                  <div>
                    <h3 className="font-semibold">Need Help with Eligibility?</h3>
                    <p className="text-sm text-muted-foreground">
                      Our hospice team is available 24/7 to discuss patient eligibility
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" onClick={() => window.open("tel:703-578-7108")}>
                      <Phone className="w-4 h-4 mr-2" /> Call: 703-578-7108
                    </Button>
                    <Button variant="outline" onClick={() => window.open("sms:703-578-7108")}>
                      <MessageSquare className="w-4 h-4 mr-2" /> Text: 703-578-7108
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
        
        
      </div>
    );
  }

  // Questionnaire Step
  if (step === "questionnaire" && questionnaire) {
    const questions = questionnaire.questions || [];
    const currentQuestion = questions[currentQuestionIndex];
    const progress = ((currentQuestionIndex + 1) / questions.length) * 100;
    const isLastQuestion = currentQuestionIndex === questions.length - 1;
    const allAnswered = Object.keys(answers).length === questions.length;

    return (
      <div className="min-h-screen bg-gradient-to-b from-background to-muted/20">
        
        
        <div className="container mx-auto px-4 py-8">
          <div className="max-w-3xl mx-auto">
            {/* Header */}
            <div className="mb-6">
              <Button variant="ghost" onClick={resetAssessment} className="mb-4">
                <ArrowLeft className="w-4 h-4 mr-2" /> Back to Categories
              </Button>
              <h1 className="text-2xl font-bold">{questionnaire.title}</h1>
              <p className="text-muted-foreground">{questionnaire.description}</p>
            </div>

            {/* Progress */}
            <div className="mb-6">
              <div className="flex justify-between text-sm mb-2">
                <span>Question {currentQuestionIndex + 1} of {questions.length}</span>
                <span>{Math.round(progress)}% Complete</span>
              </div>
              <Progress value={progress} className="h-2" />
            </div>

            {/* Patient Name Input (first question only) */}
            {currentQuestionIndex === 0 && (
              <Card className="mb-6">
                <CardContent className="pt-6">
                  <Label htmlFor="patient-name">Patient Name (Optional)</Label>
                  <Input
                    id="patient-name"
                    placeholder="Enter patient name for reference"
                    value={patientName}
                    onChange={(e) => setPatientName(e.target.value)}
                    className="mt-2"
                    data-testid="patient-name-input"
                  />
                </CardContent>
              </Card>
            )}

            {/* Current Question */}
            <Card className="mb-6">
              <CardHeader>
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-primary font-semibold">
                    {currentQuestionIndex + 1}
                  </div>
                  <CardTitle className="text-lg">{currentQuestion.question}</CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                {currentQuestion.type === "boolean" && (
                  <div className="space-y-3">
                    <div 
                      className={`flex items-center space-x-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                        answers[currentQuestion.id] === true 
                          ? 'border-primary bg-primary/10' 
                          : 'hover:bg-muted/50'
                      }`}
                      onClick={() => handleAnswer(currentQuestion.id, true)}
                      data-testid={`${currentQuestion.id}-yes`}
                    >
                      <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                        answers[currentQuestion.id] === true 
                          ? 'border-primary' 
                          : 'border-muted-foreground'
                      }`}>
                        {answers[currentQuestion.id] === true && (
                          <div className="w-2 h-2 rounded-full bg-primary" />
                        )}
                      </div>
                      <span className="flex-1 font-medium">Yes</span>
                    </div>
                    <div 
                      className={`flex items-center space-x-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                        answers[currentQuestion.id] === false 
                          ? 'border-primary bg-primary/10' 
                          : 'hover:bg-muted/50'
                      }`}
                      onClick={() => handleAnswer(currentQuestion.id, false)}
                      data-testid={`${currentQuestion.id}-no`}
                    >
                      <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                        answers[currentQuestion.id] === false 
                          ? 'border-primary' 
                          : 'border-muted-foreground'
                      }`}>
                        {answers[currentQuestion.id] === false && (
                          <div className="w-2 h-2 rounded-full bg-primary" />
                        )}
                      </div>
                      <span className="flex-1 font-medium">No</span>
                    </div>
                  </div>
                )}

                {currentQuestion.type === "number" && (
                  <Input
                    type="number"
                    min="0"
                    placeholder="Enter number"
                    value={answers[currentQuestion.id] || ""}
                    onChange={(e) => handleAnswer(currentQuestion.id, parseInt(e.target.value) || 0)}
                    className="max-w-xs"
                  />
                )}

                {currentQuestion.type === "select" && (
                  <RadioGroup
                    value={answers[currentQuestion.id] || ""}
                    onValueChange={(v) => handleAnswer(currentQuestion.id, v)}
                    className="space-y-3"
                  >
                    {currentQuestion.options?.map((option, idx) => (
                      <div 
                        key={idx}
                        className="flex items-center space-x-3 p-3 rounded-lg border hover:bg-muted/50 cursor-pointer"
                        onClick={() => handleAnswer(currentQuestion.id, option)}
                      >
                        <RadioGroupItem value={option} id={`${currentQuestion.id}-${idx}`} />
                        <Label htmlFor={`${currentQuestion.id}-${idx}`} className="cursor-pointer flex-1">
                          {option}
                        </Label>
                      </div>
                    ))}
                  </RadioGroup>
                )}
              </CardContent>
              <CardFooter className="flex justify-between">
                <Button
                  variant="outline"
                  onClick={() => setCurrentQuestionIndex(Math.max(0, currentQuestionIndex - 1))}
                  disabled={currentQuestionIndex === 0}
                >
                  <ArrowLeft className="w-4 h-4 mr-2" /> Previous
                </Button>
                
                {isLastQuestion ? (
                  <Button 
                    onClick={submitAssessment}
                    disabled={isLoading || !allAnswered}
                    data-testid="submit-assessment"
                  >
                    {isLoading ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Analyzing...</>
                    ) : (
                      <><Sparkles className="w-4 h-4 mr-2" /> Get AI Analysis</>
                    )}
                  </Button>
                ) : (
                  <Button
                    onClick={() => setCurrentQuestionIndex(currentQuestionIndex + 1)}
                    disabled={answers[currentQuestion.id] === undefined}
                  >
                    Next <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                )}
              </CardFooter>
            </Card>

            {/* Question Overview */}
            <Card>
              <CardHeader>
                <CardTitle className="text-sm">Question Overview</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-2">
                  {questions.map((q, idx) => (
                    <Button
                      key={q.id}
                      variant={answers[q.id] !== undefined ? "default" : "outline"}
                      size="sm"
                      className={`w-8 h-8 p-0 ${currentQuestionIndex === idx ? "ring-2 ring-primary" : ""}`}
                      onClick={() => setCurrentQuestionIndex(idx)}
                    >
                      {idx + 1}
                    </Button>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
        
        
      </div>
    );
  }

  // Results Step
  if (step === "results" && results) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-background to-muted/20">
        
        
        <div className="container mx-auto px-4 py-8">
          <div className="max-w-4xl mx-auto">
            {/* Eligibility Score */}
            <Card className={`mb-6 border-2 ${getUrgencyColor(results.urgency)}`}>
              <CardContent className="pt-8 pb-8">
                <div className="text-center">
                  <div className="flex justify-center mb-4">
                    {getUrgencyIcon(results.urgency)}
                  </div>
                  <h2 className="text-2xl font-bold mb-2">{results.recommendation}</h2>
                  <div className="text-5xl font-bold mb-4">
                    {results.eligibility_percentage}%
                  </div>
                  <p className="text-lg">Hospice Eligibility Score</p>
                  <Badge className="mt-4" variant={results.urgency === "high" ? "destructive" : "secondary"}>
                    {results.urgency?.toUpperCase()} PRIORITY
                  </Badge>
                </div>
              </CardContent>
            </Card>

            {/* AI Insights */}
            {results.ai_insights && (
              <Card className="mb-6">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Sparkles className="w-5 h-5 text-primary" />
                    AI Clinical Insights
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="prose prose-sm max-w-none">
                    <p className="whitespace-pre-wrap">{results.ai_insights.summary}</p>
                  </div>
                  
                  {results.ai_insights.suggested_actions?.length > 0 && (
                    <div className="mt-6">
                      <h4 className="font-semibold mb-3">Recommended Next Steps:</h4>
                      <div className="space-y-2">
                        {results.ai_insights.suggested_actions.map((action, idx) => (
                          <div key={idx} className="flex items-start gap-2 p-3 bg-muted/50 rounded-lg">
                            <CheckCircle className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                            <span>{action}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Criteria Met / Not Met */}
            <div className="grid gap-6 md:grid-cols-2 mb-6">
              <Card>
                <CardHeader>
                  <CardTitle className="text-green-600 flex items-center gap-2">
                    <CheckCircle className="w-5 h-5" />
                    Criteria Met ({results.criteria_met?.length || 0})
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2">
                    {results.criteria_met?.map((item, idx) => (
                      <li key={idx} className="text-sm flex items-start gap-2">
                        <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                        {item}
                      </li>
                    ))}
                    {(!results.criteria_met || results.criteria_met.length === 0) && (
                      <li className="text-sm text-muted-foreground">No criteria met</li>
                    )}
                  </ul>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-gray-600 flex items-center gap-2">
                    <XCircle className="w-5 h-5" />
                    Criteria Not Met ({results.criteria_not_met?.length || 0})
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2">
                    {results.criteria_not_met?.map((item, idx) => (
                      <li key={idx} className="text-sm flex items-start gap-2">
                        <XCircle className="w-4 h-4 text-gray-400 flex-shrink-0 mt-0.5" />
                        {item}
                      </li>
                    ))}
                    {(!results.criteria_not_met || results.criteria_not_met.length === 0) && (
                      <li className="text-sm text-muted-foreground">All criteria met</li>
                    )}
                  </ul>
                </CardContent>
              </Card>
            </div>

            {/* Contact Actions */}
            <Card>
              <CardHeader>
                <CardTitle>Ready to Refer?</CardTitle>
                <CardDescription>
                  Contact Goodwin Hospice to discuss this patient or submit a referral
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-3">
                  <Button 
                    size="lg" 
                    onClick={() => window.open("tel:703-578-7108")}
                    data-testid="call-hospice-btn"
                  >
                    <Phone className="w-4 h-4 mr-2" /> Call: 703-578-7108
                  </Button>
                  <Button 
                    size="lg" 
                    variant="outline"
                    onClick={() => window.open("sms:703-578-7108?body=URGENT REFERRAL - Patient meets hospice criteria")}
                  >
                    <MessageSquare className="w-4 h-4 mr-2" /> Text: 703-578-7108
                  </Button>
                  <Button 
                    size="lg" 
                    variant="secondary"
                    onClick={() => navigate("/facility/referral/new")}
                  >
                    <FileText className="w-4 h-4 mr-2" /> Submit Referral
                  </Button>
                </div>
                
                <Separator className="my-6" />
                
                <div className="text-sm text-muted-foreground">
                  <p><strong>Fax:</strong> 703-578-7109</p>
                  <p><strong>Email:</strong> hospice@goodwinliving.org</p>
                  <p><strong>Emergency Line:</strong> 703-578-7108</p>
                </div>
              </CardContent>
              <CardFooter>
                <Button variant="outline" onClick={resetAssessment}>
                  Start New Assessment
                </Button>
              </CardFooter>
            </Card>
          </div>
        </div>
        
        
      </div>
    );
  }

  return null;
}
